// @flow

import React, { PureComponent, Fragment } from "react";
import type { NullaryFn, UnaryFn } from "@tvg/types/Functional";
import { noop } from "lodash";
import Icon from "@fdr/static-ui/Icons";
import { arrowBack, arrowForward } from "@fdr/static-ui/Icons/icons";
import DefaultButton from "../../_atoms/ButtonDefault";
import Button from "../../_atoms/Button";

import {
  PaginationWrapper,
  ButtonPage,
  TopRacesButtonDefault,
  PagesWrapper,
  NextWrapper,
  PaginationBullet,
  NextBtnWrapper,
  PrevBtnWrapper
} from "./styled-components";

type PaginationThemesEnum = "default" | "tutorials" | "topRaces";

type FinalStepInfo = {
  buttonNav: string,
  buttonLabel: string,
  finalStepButtonClick: NullaryFn<mixed>,
  qaLabel: string
};

type Props = {
  /**
   * Page that is currently selected
   */
  pageSelected: number,
  /**
   * Total number of pages
   */
  totalPages: number,
  /**
   * Callback to go back in pagination
   */
  onBackPage: NullaryFn<mixed>,
  /**
   * Callback to go forward in pagination
   */
  onForwardPage: NullaryFn<mixed>,
  /**
   * Callback when a page is selected
   */
  onSelectPage: ?UnaryFn<number, mixed>,
  /**
   * Visual theme for the pagination component
   */
  theme: PaginationThemesEnum,
  /**
   * Classname to style the component
   */
  className: string,
  /**
   * QA Label
   */
  qaLabel: string,
  /**
   * disable back button
   */
  isBackDisabled: boolean,
  /**
   * disable forward button
   */
  isForwardDisabled: boolean,
  finalStepInfo: FinalStepInfo
};

export default class Pagination extends PureComponent<Props> {
  static defaultProps = {
    pageSelected: 1,
    totalPages: 1,
    onBackPage: noop,
    onForwardPage: noop,
    onSelectPage: undefined,
    theme: "default",
    className: "",
    qaLabel: "pagination",
    isBackDisabled: false,
    isForwardDisabled: false,
    finalStepInfo: {
      buttonNav: "",
      buttonLabel: "",
      finalStepButtonClick: noop,
      qaLabel: ""
    }
  };

  defaultPaginationRender = () => {
    const {
      theme,
      onBackPage,
      pageSelected,
      onSelectPage,
      onForwardPage,
      totalPages
    } = this.props;
    return (
      <PaginationWrapper
        theme={theme}
        className={this.props.className}
        data-qa-label={this.props.qaLabel}
      >
        <DefaultButton onClick={onBackPage} isDisabled={pageSelected === 1}>
          <Icon icon={arrowBack} />
        </DefaultButton>
        <PagesWrapper>
          {onSelectPage ? (
            this.renderButtons()
          ) : (
            <Fragment>
              <strong>{pageSelected}</strong>&nbsp;of&nbsp;
              <strong>{totalPages}</strong>
            </Fragment>
          )}
        </PagesWrapper>
        <DefaultButton
          onClick={onForwardPage}
          isDisabled={pageSelected === totalPages}
        >
          <Icon icon={arrowForward} />
        </DefaultButton>
      </PaginationWrapper>
    );
  };

  topRacesPaginationRender = () => (
    <PaginationWrapper
      theme={this.props.theme}
      className={this.props.className}
      data-qa-label={this.props.qaLabel}
    >
      <TopRacesButtonDefault
        onClick={this.props.onBackPage}
        isDisabled={this.props.isBackDisabled}
        size={16}
        qaLabel="pagination-previousButton"
      >
        <Icon icon={arrowBack} />
      </TopRacesButtonDefault>
      <TopRacesButtonDefault
        onClick={this.props.onForwardPage}
        isDisabled={this.props.isForwardDisabled}
        size={16}
        qaLabel="pagination-forwardButton"
      >
        <Icon icon={arrowForward} />
      </TopRacesButtonDefault>
    </PaginationWrapper>
  );

  tutorialsPaginationRender = () => {
    const {
      theme,
      onBackPage,
      pageSelected,
      onForwardPage,
      totalPages,
      finalStepInfo
    } = this.props;

    return (
      <PaginationWrapper
        theme={theme}
        className={this.props.className}
        data-qa-label={this.props.qaLabel}
      >
        <PrevBtnWrapper>
          <Button
            onClick={onBackPage}
            isDisabled={pageSelected === 1}
            qaLabel="pagination-previousButton"
          >
            {pageSelected !== 1 && <Icon icon={arrowBack} />}
          </Button>
        </PrevBtnWrapper>
        <PagesWrapper>{this.renderButtons()}</PagesWrapper>
        {pageSelected !== totalPages ? (
          <NextBtnWrapper>
            <Button
              size="medium"
              onClick={onForwardPage}
              isDisabled={pageSelected === totalPages}
              qaLabel="pagination-nextButton"
            >
              <NextWrapper>Next</NextWrapper>
            </Button>
          </NextBtnWrapper>
        ) : (
          <NextBtnWrapper>
            <Button
              size="medium"
              type="primary"
              text={finalStepInfo.buttonLabel}
              url={finalStepInfo.buttonNav}
              onClick={finalStepInfo.finalStepButtonClick}
              qaLabel={finalStepInfo.qaLabel}
              isInternalLink={finalStepInfo.buttonNav !== ""}
            />
          </NextBtnWrapper>
        )}
      </PaginationWrapper>
    );
  };

  renderButtons = () => {
    const buttonsArray = [];
    const { theme, totalPages, pageSelected, onSelectPage } = this.props;

    switch (theme) {
      case "tutorials":
        for (let i = 1; i <= totalPages; i += 1) {
          buttonsArray.push(
            <PaginationBullet key={`wbp-${i}`} active={pageSelected === i} />
          );
        }
        break;
      default:
        for (let i = 1; i <= totalPages; i += 1) {
          buttonsArray.push(
            <ButtonPage
              key={`wbp-${i}`}
              onClick={() => onSelectPage && onSelectPage(i)}
              type={i === pageSelected ? "primary" : "secondary"}
            >
              {i}
            </ButtonPage>
          );
        }
        break;
    }

    return <Fragment>{buttonsArray}</Fragment>;
  };

  render() {
    const { theme } = this.props;
    let returnRender;

    switch (theme) {
      case "tutorials":
        returnRender = this.tutorialsPaginationRender();
        break;
      case "topRaces":
        returnRender = this.topRacesPaginationRender();
        break;
      default:
        returnRender = this.defaultPaginationRender();
        break;
    }
    return returnRender;
  }
}
